// MainTable.tsx
import { useEffect, useMemo, useState } from 'react';
import { MainTableItem, BottomsMainItem, NglSchedule } from '../../../contexts/apiService';
import RowDetails from './rowDetails';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { Virtuoso } from 'react-virtuoso';

interface MainTableProps {
    mainTableData?: MainTableItem[];
    bottomsData?: BottomsMainItem[];
    nglData?: NglSchedule[];
    selectedProducers: string[];
    className?: string;
    onRowSelect?: (item: MainTableItem | BottomsMainItem | NglSchedule | null, index: string | null) => void;
    selectedProduct: string;
}

// Configuration for table columns
interface TableColumnConfig {
    key: string;
    label: string;
    formatter?: (value: any) => string;
    alignment?: 'left' | 'right' | 'center';
    columnWidth?: string;
    sortable?: boolean;
    headerless?: boolean;
    renderCell?: (item: any) => React.ReactNode;
}

// Sort states for cycling through
type SortState = 'off' | 'desc' | 'asc';

// Sort configuration for each column
interface SortConfig {
    [key: string]: SortState;
}

const getCrudOilColumns = (): TableColumnConfig[] => [
    // Columns specific to crude data
    {
        key: 'id',
        label: '',
        alignment: 'center',
        columnWidth: '40px',
        headerless: true,
        renderCell: (item: MainTableItem) => {
            const getStatusColor = () => {
                const status = item.status;
                
                if (!status) return '#ccc';
                
                const statusLower = status.toString().toLowerCase();
                
                if (statusLower === 'completed') {
                    return '#2cc740';
                } else if (statusLower === 'rejected') {
                    return '#ff5f57';
                } else {
                    return '#ffc107'; 
                }
            };
     
            return (
                <div style={{ height:'100%',display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div 
                        style={{
                            width: '10px',
                            height: '10px',
                            borderRadius: '50%',
                            backgroundColor: getStatusColor(),
                            border: '1px solid rgba(0,0,0,0.1)'
                        }}
                        title={`Status: ${status || 'Unknown'}`}
                    />
                </div>
            );
        }
    },
    {
        key: 'load_date',
        label: 'Date',
        formatter: (value) => value ? new Date(value).toLocaleDateString() : '',
        alignment: 'left',
        columnWidth: '1fr',
    },
    {
        key: 'ticket_number',
        label: 'Ticket #',
        alignment: 'left',
        columnWidth: '1fr',
    },
    {
        key: 'producer',
        label: 'Producer',
        alignment: 'left',
        columnWidth: '1.5fr',
    },
    {
        key: 'lease',
        label: 'Lease Name',
        alignment: 'left',
        columnWidth: '2fr',
    },
    {
        key: 'destination',
        label: 'Destination',
        alignment: 'left',
        columnWidth: '2fr',
    },
    {
        key: 'net_barrels',
        label: 'Net Barrels',
        formatter: (value) => value?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00',
        alignment: 'right',
        columnWidth: 'minmax(100px, max-content)',
    },
    {
        key: 'net_profit',
        label: 'Net Profit',
        formatter: (value) => {
            const numValue = Number(value) || 0;
            return `$ ${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        alignment: 'right',
        columnWidth: 'minmax(100px, max-content)',
        sortable: true,
    },
    {
        key: 'margin',
        label: 'Margin',
        formatter: (value) => `$ ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}`,
        alignment: 'right',
        columnWidth: 'minmax(80px, max-content)',
        sortable: true,
    },
];

const getBottomsColumns = (): TableColumnConfig[] => [
    // Columns specific to bottoms data
    {
        key: 'final_load_date',
        label: 'Date',
        formatter: (value) => value ? new Date(value).toLocaleDateString() : '',
        alignment: 'left',
        columnWidth: 'minmax(100px, max-content)',
    },
    {
        key: 'final_ticket_number',
        label: 'Ticket #',
        alignment: 'left',
        columnWidth: 'minmax(100px, max-content)',
    },
    {
        key: 'Operator',
        label: 'Producer',
        alignment: 'left',
        columnWidth: 'minmax(180px, max-content)',
    },
    {
        key: 'Facility',
        label: 'Destination',
        alignment: 'left',
        columnWidth: 'minmax(140px, max-content)',
    },
    {
        key: 'Quantity',
        label: 'Gross Volume',
        alignment: 'left',
        columnWidth: 'minmax(110px, max-content)',
    },
    {
        key: 'Net_Barrels',
        label: 'Net Volume',
        formatter: (value) => {
            const numValue = Number(value) || 0;
            return `${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        alignment: 'right',
        columnWidth: 'minmax(120px, max-content)',
        sortable: true,
    },
    {
        key: 'net_profit',
        label: 'Net Profit',
        formatter: (value) => {
            const numValue = Number(value) || 0;
            return `$ ${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        alignment: 'right',
        columnWidth: 'minmax(100px, max-content)',
        sortable: true,
    },
];

const getNglColumns = (): TableColumnConfig[] => [
    // Columns specific to NGL data
    {
        key: 'loadDate',
        label: 'Date',
        formatter: (value) => value ? new Date(value).toLocaleDateString() : '',
        alignment: 'left',
        columnWidth: 'minmax(80px, max-content)',
    },
    {
        key: 'location',
        label: 'Location',
        alignment: 'left',
        columnWidth: 'minmax(100px, max-content)',
    },
    {
        key: 'marketDestination',
        label: 'Destination',
        alignment: 'left',
        columnWidth: 'minmax(120px, max-content)',
    },
    {
        key: 'carrier',
        label: 'Carrier',
        alignment: 'left',
        columnWidth: 'minmax(100px, max-content)',
    },
    {
        key: 'quantity',
        label: 'Est. Gallons',
        alignment: 'left',
        columnWidth: 'minmax(100px, max-content)',
    },
    {
        key: 'net_profit',
        label: 'Net Profit',
        formatter: (value) => {
            const numValue = Number(value) || 0;
            return `$ ${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        },
        alignment: 'right',
        columnWidth: 'minmax(100px, max-content)',
        sortable: true,
    },
];

const MainTable = ({ 
    mainTableData,
    bottomsData,
    nglData,
    className = '',
    onRowSelect,
    selectedProduct,
}: MainTableProps) => {
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const [sortConfig, setSortConfig] = useState<SortConfig>({});

    const TABLE_COLUMNS = useMemo(() => {
        switch (selectedProduct) {
            case 'crude_oil':
                return getCrudOilColumns();
            case 'tank_bottoms':
                return getBottomsColumns();
            case 'ngl':
                return getNglColumns();
            default:
                return getCrudOilColumns();
        }
    }, [selectedProduct]);

    // Get the appropriate data based on product type
    const currentData = useMemo(() => {
        switch (selectedProduct) {
            case 'crude_oil':
                return mainTableData || [];
            case 'tank_bottoms':
                return bottomsData || [];
            case 'ngl':
                return nglData || [];
            default:
                return [];
        }
    }, [selectedProduct, mainTableData, bottomsData, nglData]);

    // Sort data by load_date (default) or what the user selects
    const sortedData = useMemo(() => {
        if (!currentData || currentData.length === 0) {
            return [];
        }
        
        let data = [...currentData];
    
        // Get active sorts (columns that are not 'off')
        const activeSorts = Object.entries(sortConfig).filter(([_, state]) => state !== 'off');
        
        // If no active sorts, default to date sorting
        if (activeSorts.length === 0) {
            data.sort((a, b) => {
                let dateA: string | null = null;
                let dateB: string | null = null;
                
                // sort based on product type
                if (selectedProduct === 'crude_oil') {
                    dateA = (a as MainTableItem).load_date;
                    dateB = (b as MainTableItem).load_date;
                } else if (selectedProduct === 'tank_bottoms') {
                    dateA = (a as BottomsMainItem).final_load_date;
                    dateB = (b as BottomsMainItem).final_load_date;
                } else if (selectedProduct === 'ngl') {
                    dateA = (a as NglSchedule).loadDate;
                    dateB = (b as NglSchedule).loadDate;
                }
                
                // Handle null/undefined dates
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                
                // Sort by date (most recent first)
                return new Date(dateB).getTime() - new Date(dateA).getTime();
            });
        } else {
            // Apply custom sorting for active columns - FOR ALL PRODUCT TYPES
            data.sort((a, b) => {
                for (const [columnKey, sortState] of activeSorts) {
                    let valueA: number;
                    let valueB: number;
                    
                    // Get values based on column type
                    if (columnKey === 'margin' && selectedProduct === 'crude_oil') {
                        // Handle margin calculation only for crude oil
                        const netProfitA = Number((a as MainTableItem).net_profit) || 0;
                        const netBarrelsA = Number((a as MainTableItem).net_barrels) || 0;
                        valueA = netBarrelsA !== 0 ? netProfitA / netBarrelsA : 0;
                        
                        const netProfitB = Number((b as MainTableItem).net_profit) || 0;
                        const netBarrelsB = Number((b as MainTableItem).net_barrels) || 0;
                        valueB = netBarrelsB !== 0 ? netProfitB / netBarrelsB : 0;
                    } else {
                        // Handle other columns for all product types
                        valueA = Number((a as any)[columnKey]) || 0;
                        valueB = Number((b as any)[columnKey]) || 0;
                    }
                    
                    // Compare values
                    if (valueA !== valueB) {
                        if (sortState === 'desc') {
                            return valueB - valueA;
                        } else {
                            return valueA - valueB;
                        }
                    }
                }
                return 0;
            });
        }
        
        return data;
    }, [currentData, sortConfig, selectedProduct]);
    
    // Generate grid template columns dynamically
    const gridTemplateColumns = TABLE_COLUMNS
        .map(col => col.columnWidth || '1fr')
        .join(' ');

    const handleRowClick = (item: MainTableItem | BottomsMainItem | NglSchedule) => {
        const itemId = String(item.id);
        const newSelectedId = selectedRowId === itemId ? null : itemId;
        setSelectedRowId(newSelectedId);

        // Call callback with selected item and ID
        if (onRowSelect) {
            const selectedItem = newSelectedId ? item : null;
            onRowSelect(selectedItem, newSelectedId);
        }
        
    };

    const getRowClassName = (item: MainTableItem | BottomsMainItem | NglSchedule) => {
        const baseClass = 'main-table-row';
        const isSelected = selectedRowId === String(item.id);
        const selectedClass = isSelected ? 'selected' : '';
        return `${baseClass} ${selectedClass}`.trim();
    };

    const handleColumnSort = (columnKey: string) => {
        setSortConfig(current => {
            const currentState = current[columnKey] || 'off';
            
            // Cycle through: off -> desc (highest first) -> asc (lowest first) -> off
            let newState: SortState;
            if (currentState === 'off') {
                newState = 'desc';
            } else if (currentState === 'desc') {
                newState = 'asc';
            } else {
                newState = 'off';
            }
            
            // If we're activating a sort on this column, turn off all other columns
            if (newState !== 'off') {
                // Create new config with all columns set to 'off', then set this column
                const newConfig: SortConfig = {};
                Object.keys(current).forEach(key => {
                    newConfig[key] = 'off';
                });
                newConfig[columnKey] = newState;
                return newConfig;
            } else {
                // Just update this column to 'off'
                return {
                    ...current,
                    [columnKey]: newState
                };
            }
        });
    };

    const getSortIcon = (sortState: SortState) => {
        switch (sortState) {
            case 'desc': 
                return <ArrowUp size={14} />; // Highest first (down arrow)
            case 'asc': 
                return <ArrowDown size={14} />;   // Lowest first (up arrow)
            default: 
                return <ArrowUpDown size={14} />; // Off/sortable (double arrow)
        }
    };

    const getSortButtonStyle = (columnKey: string) => {
        const isActive = sortConfig[columnKey] && sortConfig[columnKey] !== 'off';
        return {
            background: 'none',
            borderRadius: '4px',
            padding: '2px 6px',
            cursor: 'pointer',
            fontSize: '12px',
            minWidth: '20px',
            height: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isActive ? '#007bff' : '#666',
            borderColor: isActive ? '#007bff' : '#ccc'
        };
    };

    // Row renderer for react-virtuoso
    const renderRow = (index: number) => {
        const item = sortedData[index];
        const isSelected = selectedRowId === String(item.id);

        return (
            <div className="test" key={item.id}>
                {/* Main Row */}
                <div
                    className={getRowClassName(item)}
                    style={{ gridTemplateColumns }}
                    onClick={() => handleRowClick(item)}
                >
                    {TABLE_COLUMNS.map(column => {
                        // Handle custom cell renderer for headerless columns
                        if (column.renderCell) {
                            return (
                                <div 
                                    key={column.key as string}
                                    className={`main-table-cell align-${column.alignment || 'left'}`}
                                >
                                    {column.renderCell(item)}
                                </div>
                            );
                        }

                        // Handle normal columns
                        let value;
                        let formattedValue;
                        
                        // Handle calculated margin column
                        if (column.key === 'margin' && selectedProduct === 'crude_oil') {
                            const mainItem = item as MainTableItem;
                            const netProfit = Number(mainItem.net_profit) || 0;
                            const netBarrels = Number(mainItem.net_barrels) || 0;
                            value = netBarrels !== 0 ? netProfit / netBarrels : 0;
                            formattedValue = column.formatter ? column.formatter(value) : String(value);
                        } else {
                            value = (item as any)[column.key];
                            formattedValue = column.formatter ? column.formatter(value) : String(value || '');
                        }
                        
                        return (
                            <div 
                                key={column.key as string}
                                className={`main-table-cell align-${column.alignment || 'left'}`}
                            >
                                {formattedValue}
                            </div>
                        );
                    })}
                </div>
                
                {/* Detail Row - Inline Expansion */}
                {isSelected && selectedProduct === 'crude_oil' && (
                    <RowDetails item={item as MainTableItem} />
                )}
            </div>
        );
    };

    if (!sortedData || sortedData.length === 0) {
        return (
            <div className={`no-data-message-container`}>
                <div className="no-data-message">No data available</div>
            </div>
        );
    }

    return (
        <div className={`main-table-container ${className}`}>
            {/* Table Header */}
            <div 
                className="main-table-header"
                style={{ gridTemplateColumns }}
            >
                {TABLE_COLUMNS.map(column => (
                    <div 
                        key={column.key as string}
                        className={`align-${column.alignment || 'left'}`}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        {/* Only show sorting controls for non-headerless, sortable columns */}
                        {column.sortable && !column.headerless && (
                            <button
                                onClick={() => handleColumnSort(column.key as string)}
                                style={getSortButtonStyle(column.key as string)}
                            >
                                {getSortIcon(sortConfig[column.key as string] || 'off')}
                            </button>
                        )}
                        {/* Only show label for non-headerless columns */}
                        {!column.headerless && <span>{column.label}</span>}
                    </div>
                ))}
            </div>

            <div className="main-table-divider" />

            {/* Virtualized Table Body for performance */}
            <div className='main-table-body'>
                <Virtuoso
                    style={{ height: '100%' }}
                    totalCount={sortedData.length}
                    itemContent={renderRow}
                    overscan={10}
                />
            </div>
        </div>
    );
};

export default MainTable;